import React, { useMemo } from "react";
import { Box, Flex, Text, Badge, Heading } from "@radix-ui/themes";

/**
 * Shared permission picker used by both the user-account modal (fine-tuning a
 * user's effective permissions on top of their roles) and the role editor
 * (defining a role's default permissions). The catalog comes from the server
 * (GET /permissioncatalog) — this component never hardcodes permission names.
 *
 * Props:
 *   catalog   [{ name, group, description }]  — the permission catalog
 *   checked   Set<string>                     — permissions currently ticked
 *   onToggle  (name) => void                  — toggle a single permission
 *   baseSet   Set<string> | null              — role defaults; when provided, a
 *                                               permission whose state differs
 *                                               from the role default is marked
 *                                               "Overridden" (user modal only)
 *   disabled  bool                            — disable all inputs
 */
export default function PermissionGrid({ catalog = [], checked, onToggle, baseSet = null, disabled = false }) {
  //Preserve the catalog's group order while grouping (Inventory, Deals, ...).
  const groups = useMemo(() => {
    const order = [];
    const byGroup = new Map();
    for (const p of catalog) {
      if (!byGroup.has(p.group)) { byGroup.set(p.group, []); order.push(p.group); }
      byGroup.get(p.group).push(p);
    }
    return order.map((g) => [g, byGroup.get(g)]);
  }, [catalog]);

  return (
    <Flex direction="column" gap="4">
      {groups.map(([group, perms]) => (
        <Box key={group}>
          <Heading size="2" mb="2" color="gray">{group}</Heading>
          <Flex direction="column" gap="1">
            {perms.map((p) => {
              const on = checked.has(p.name);
              const overridden = baseSet ? (baseSet.has(p.name) !== on) : false;
              return (
                <label
                  key={p.name}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: disabled ? 'default' : 'pointer', padding: '2px 0' }}
                  title={p.name}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={disabled}
                    onChange={() => onToggle(p.name)}
                  />
                  <Text size="2" style={{ flex: 1 }}>{p.description}</Text>
                  {overridden && (
                    <Badge color={on ? 'green' : 'red'} variant="soft" size="1">
                      {on ? 'Granted' : 'Denied'}
                    </Badge>
                  )}
                </label>
              );
            })}
          </Flex>
        </Box>
      ))}
    </Flex>
  );
}
